import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from 'reactstrap';
import { pricingPlansPath } from '../../../routes';

// Pill showing whether an expert (or their account) is on a paying plan.
//
// isPaid is normalized from the has_paid_representative API attribute in services/promo_entity.js. It is
// undefined for products, for entities not yet persisted, and whenever the API was not asked to report on paid
// status - all of which render no pill at all. That is deliberately distinct from false, which renders "Basic".
function PlanBadge({
  isPaid, isViewersOwnProfile, firstName, className,
}) {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const basicBadgeRef = useRef();

  if (isPaid === undefined) {
    return null;
  }

  // btn-orange-gradient is the app's gold-to-coral upgrade gradient; it carries its own white bold text.
  if (isPaid) {
    return (
      <span className={`badge rounded-pill btn-orange-gradient pro-plan-badge ${className}`}>
        <i className="fa-solid fa-badge-check me-1" />
        PRO
      </span>
    );
  }

  return (
    <>
      {/* A button, not a span: it has to take keyboard focus and a tap for the tooltip to be reachable without a
          mouse. Both handlers stop propagation because the picker card around it attaches the entity on click AND
          on Enter/Space - without the keydown guard, reaching this badge by keyboard would attach the expert.
          The click opens the tooltip outright rather than toggling: the focus trigger has already opened it by
          then, so toggling would close it again on the very tap that is meant to reveal it. */}
      <button
        type="button"
        ref={basicBadgeRef}
        className={`badge rounded-pill border-0 bg-gray-300 text-dark ${className}`}
        onClick={(event) => {
          event.stopPropagation();
          setTooltipOpen(true);
        }}
        onKeyDown={(event) => event.stopPropagation()}
      >
        Basic
      </button>
      {/* autohide={false} keeps the tooltip up while the pointer moves into it, so the link stays clickable.
          Tooltips here are white with primary-colored text, so text-dark leaves the link the only highlight. */}
      <Tooltip
        placement="top"
        isOpen={tooltipOpen}
        target={basicBadgeRef}
        toggle={() => setTooltipOpen(!tooltipOpen)}
        trigger="hover focus"
        autohide={false}
        innerClassName="text-dark"
      >
        {isViewersOwnProfile
          ? "You've claimed your basic Qwoted profile."
          : `${firstName || 'This expert'} has claimed a basic Qwoted profile.`}
        {' Upgrade to respond to more opportunities and unlock additional features. '}
        <a
          href={pricingPlansPath}
          className="text-decoration-underline"
          target="_blank"
          rel="noreferrer"
        >
          View plans
        </a>
        .
      </Tooltip>
    </>
  );
}

PlanBadge.propTypes = {
  isPaid: PropTypes.bool,
  // True when the badged expert is the viewer's own profile, which the tooltip addresses in the first person.
  isViewersOwnProfile: PropTypes.bool,
  firstName: PropTypes.string,
  className: PropTypes.string,
};

PlanBadge.defaultProps = {
  isPaid: undefined,
  isViewersOwnProfile: false,
  firstName: 'This expert',
  className: '',
};

export default PlanBadge;
